'use client';

import { Check, ChevronsUpDown, PlusIcon, XIcon } from 'lucide-react';
import {
  Badge,
  Button,
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@@/ui/primitives';
import React, { useState } from 'react';
import { cn } from '@@/lib/utils';

const MATCH_TYPES = [
  { value: 'MATCH_ALL', label: 'Match all' },
  { value: 'MATCH_ANY', label: 'Match any' },
  { value: 'MATCH_EXACT', label: 'Match exact' },
  { value: 'MATCH_NONE', label: 'Match none' }
] as const;

export type MatchType = 'MATCH_ALL' | 'MATCH_ANY' | 'MATCH_EXACT' | 'MATCH_NONE';

export interface AttributeCriteria {
  Key: string;
  Values: string[];
}

export interface AttributesFilter {
  criteria: AttributeCriteria[];
  matchType: MatchType;
}

export function FilterAttributes(props: {
  disabled: boolean;
  selected: AttributesFilter;
  onSelect: (value: AttributesFilter) => void;
}) {
  const [matchTypeOpen, setMatchTypeOpen] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const criteria = props.selected.criteria;
  const matchType = props.selected.matchType;

  const updateCriteria = (newCriteria: AttributeCriteria[]) => {
    props.onSelect({
      criteria: newCriteria,
      matchType
    });
  };

  const updateMatchType = (newMatchType: MatchType) => {
    props.onSelect({
      criteria,
      matchType: newMatchType
    });
  };

  const addCriterion = () => {
    if (!newKey.trim() || !newValue.trim()) return;

    const values = newValue
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);

    if (values.length === 0) return;

    const newCriteria = [...criteria, { Key: newKey.trim(), Values: values }];
    updateCriteria(newCriteria);
    setNewKey('');
    setNewValue('');
    setShowInput(false);
  };

  const removeCriterion = (index: number) => {
    const newCriteria = criteria.filter((_, i) => i !== index);
    updateCriteria(newCriteria);
  };

  const cancelAdd = () => {
    setNewKey('');
    setNewValue('');
    setShowInput(false);
  };

  return (
    <div className='flex flex-col gap-2.5'>
      <div className='flex items-center gap-2'>
        <Popover open={matchTypeOpen} onOpenChange={setMatchTypeOpen}>
          <PopoverTrigger asChild>
            <Button
              variant='outline'
              role='combobox'
              aria-expanded={matchTypeOpen}
              className='text-sm! justify-between flex-1'
              disabled={props.disabled || criteria.length === 0}
            >
              {MATCH_TYPES.find((t) => t.value === matchType)?.label}
              <ChevronsUpDown className='opacity-50' />
            </Button>
          </PopoverTrigger>
          <PopoverContent className='p-0 font-sans w-48' align='start'>
            <Command>
              <CommandList>
                <CommandGroup>
                  {MATCH_TYPES.map((type) => (
                    <CommandItem
                      key={type.value}
                      value={type.value}
                      onSelect={() => {
                        updateMatchType(type.value);
                        setMatchTypeOpen(false);
                      }}
                      className='font-medium'
                    >
                      {type.label}
                      <Check className={cn('ml-auto', matchType === type.value ? 'opacity-100' : 'opacity-0')} />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <Button
          variant='ghost'
          size='sm'
          className='text-sm!'
          disabled={props.disabled || criteria.length >= 15}
          onClick={() => setShowInput(true)}
        >
          <PlusIcon className='size-4' />
          Add
        </Button>
      </div>

      {showInput && (
        <div className='flex flex-col gap-2 border border-neutral-200 rounded-md p-2'>
          <div className='flex flex-row gap-2'>
            <Input
              placeholder='Key'
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              className='text-xs! h-8'
              disabled={props.disabled}
              autoFocus
            />
            <Input
              placeholder='Values (comma-separated, max 20)'
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              className='text-xs! h-8'
              disabled={props.disabled}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCriterion();
                } else if (e.key === 'Escape') {
                  cancelAdd();
                }
              }}
            />
          </div>
          <div className='flex gap-1.5 justify-end'>
            <Button
              variant='ghost'
              size='xs'
              className='text-xs! font-medium! px-3! h-7! rounded-sm'
              onClick={cancelAdd}
            >
              Cancel
            </Button>
            <Button
              variant='default'
              size='xs'
              className='text-xs! font-medium! px-3! h-7! rounded-sm'
              onClick={addCriterion}
              disabled={!newKey.trim() || !newValue.trim()}
            >
              <PlusIcon className='size-3.5' />
              Add
            </Button>
          </div>
        </div>
      )}

      {criteria.length > 0 && (
        <div className='flex flex-wrap gap-1.5'>
          {criteria.map((criterion, index) => (
            <Badge key={index} variant='secondary' className='pl-2.5 pr-1.5 py-1 gap-1.5 text-xs font-medium'>
              <span className='font-medium'>{criterion.Key}:</span>
              <span>{criterion.Values.join(', ')}</span>
              <button
                onClick={() => removeCriterion(index)}
                disabled={props.disabled}
                className='ml-0.5 hover:bg-muted-foreground/20
                  rounded-full p-0.5 transition-colors'
              >
                <XIcon className='size-3' />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
